/**
 *
 * StoreForm
 *
 */

import { Input as AInput, Col, Form, Modal, Row, message } from 'antd';
import React, { ForwardRefRenderFunction, useEffect, useImperativeHandle, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styles/styled-components';

import Input from 'components/TrackingComponents/Input';
import utilsMessages from 'utils/messages';
import messages from './messages';
// import LocationFormSection from '../LocationFormSection';
import AddressForMap from 'components/AddressForMap';
import { Avatar } from 'components/Company/Avatar';
import RedInvoice from 'components/CreateStorePopup/components/RedInvoice';
import StoreMapWrapper from 'components/CreateStorePopup/components/StoreMapWrapper';
import { FIELD_NAME_FULL_TEXT } from 'components/CreateStorePopup/constants';
import useWatchFieldChange from 'components/CreateStorePopup/hooks/useWatchFieldChange';
import Dismiss from 'components/CreateStorePopup/images/Dismiss.svg';
import confirmMessage from 'components/CreateStorePopup/messages';
import { NewImageUploadButton } from 'containers/ChangeStorePage';
import changeStoreMessages from 'containers/ChangeStorePage/messages';
import translations from 'translations';
import { trimRecursively } from 'utils/trimRecursively';
import { GoogleMapsContextProvider } from '../../containers/LandingPage/DistributionLocationPage/components/Map/hooks/GoogleMapScriptContext';
import { DEFAULT_REGION_COORDS, INITIAL_CAMERA } from '../CreateStorePopup/constants';
import SVGIcon from '../SVGIcon/SVGIcon';
const { confirm } = Modal;

const AntdInput = styled(AInput)<any>``;

const InfoContainer = styled.div`
  margin-bottom: 6pt;
`;

const Heading = styled.h4`
  margin-bottom: 6pt;
  color: #343538;
  font-family: 'Maven Pro';
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%;
`;

const newAvatarWrapperStyle = css`
  height: 212px;
  padding: 20px;
  border-color: #eaeaeb;
  background: #fcfcfd;
`;

const DivideLine = styled.div`
  width: 100%;
  height: 1px;
  background: #eaebea;
  margin: 0 0 20px;
`;

const RedInvoiceTitle = styled.div`
  color: #7c7c7c;
  font-size: 20px;
  font-weight: 600;
  line-height: 26px;
  margin-bottom: 8px;
`;

const AvatarEl = styled.div`
  & > div > div > img {
    height: 170px !important;
    object-fit: contain;
  }
`;

interface OwnProps {
  store: {
    name: string;
    location: {
      address: string;
      city: string;
      district: string;
      ward: string;
      geoLat: number;
      geoLon: number;
      noteForAddress: string;
    };
    telephone: string;
  };
  onCancel: () => void;
  onSubmit: (data: any) => void;
  name: string;
  setLoading: (loading: boolean) => void;
  isAlreadySubmit?: boolean;
  regions?: string[];
  onFormStatusChecker?: (data: any) => void;
  imageUrl?: string;
  onSetImageUrl?: (url: string) => void;
  setFetchingInvoice?: (state: boolean) => void;
}

const StoreForm: ForwardRefRenderFunction<any, OwnProps> = (
  {
    store,
    onCancel: _onCancel,
    onSubmit: _onSubmit,
    setLoading,
    onFormStatusChecker,
    name,
    regions,
    isAlreadySubmit,
    imageUrl,
    onSetImageUrl,
    setFetchingInvoice
  },
  ref,
) => {
  const [form] = Form.useForm();
  const formRef = React.useRef<any>(null);
  useImperativeHandle(ref, () => form, [form]);
  const defaultCoords = regions?.length ? DEFAULT_REGION_COORDS[regions[0]] : null;
  const [openExtendedMap, setOpenExtendedMap] = useState(false);
  const [placeId, setPlaceId] = useState<string>('');
  const [coords, setCoords] = useState<google.maps.LatLngLiteral | null>(defaultCoords);
  const [isOutOfRegion, setIsOutOfRegion] = useState<boolean>(false);
  const [messageApi, contextHolder] = message.useMessage();

  const { isReadyToSubmit } = useWatchFieldChange(form, regions);

  useEffect(() => {
    if (onFormStatusChecker) {
      onFormStatusChecker({
        isReadyToSubmit: isReadyToSubmit,
      });
    }
  }, [isReadyToSubmit]);

  useEffect(() => {
    form.setFieldValue('geoLat', coords?.lat);
    form.setFieldValue('geoLon', coords?.lng);
  }, [coords?.lat, coords?.lng]);

  const onSubmit = (values) => {
    const { taxCode, redInvoiceCompanyName, redInvoiceEmail, redInvoiceAddress } = values;
    const isValidRedInvoice = validateRedInvoice({
      taxCode: taxCode,
      redInvoiceCompanyName: redInvoiceCompanyName,
      redInvoiceEmail: redInvoiceEmail,
      redInvoiceAddress: redInvoiceAddress,
    });
    if (!isValidRedInvoice) {
      return;
    }
    form.resetFields();
    setCoords(defaultCoords);
    setPlaceId('');
    _onSubmit(trimRecursively(values));
  };

  const validateRedInvoice = (redInvoice: any) => {
    const hasValueFields = Object.values(redInvoice).some((value) => value);
    let isValid = true;
    if (hasValueFields) {
      Object.entries(redInvoice).forEach(([key, value]) => {
        if (!value && ['redInvoiceCompanyName', 'taxCode'].includes(key)) {
          isValid = false;
          form.setFields([
            {
              name: key,
              errors: [translations(messages.pleaseFillOutThisField)],
            },
          ]);
        }
      });
    }
    return isValid;
  };

  const handleShowWarning = () => {
    if (!openExtendedMap) {
      messageApi.open({
        type: 'error',
        content: (
          <FormattedMessage
            {...confirmMessage.warningTitle}
            values={{ region: translations(utilsMessages[regions?.[0] || '']) }}
          />
        ),
        className: 'out-of-region-warning',
        icon: (
          <SVGIcon
            style={{ cursor: 'pointer' }}
            src={Dismiss}
            renderAsImg={false}
            onClick={() => messageApi.destroy()}
          />
        ),
        style: {
          marginInline: 'auto',
        },
      });
    }
  };

  const formatPhoneNumber = () => {
    if (form.getFieldError('telephone').length) {
      return;
    }
    const regex = /[()+\-.\s]/g;
    const phoneNumberValue = form.getFieldValue('telephone');
    form.setFieldValue('telephone', phoneNumberValue.replace(regex, ''));
  };
  return (
    <GoogleMapsContextProvider
      apiKey={process.env.GOOGLE_MAP_API || ''}
      language="vi"
      region="VN"
      libraries={['maps', 'geocoding', 'marker', 'places']}
      mapCameraProps={INITIAL_CAMERA}
    >
      {contextHolder}
      <Form
        form={form}
        ref={formRef}
        layout="vertical"
        requiredMark={false}
        onFinish={(values) => onSubmit(values)}
        size="large"
        name={name}
      >
        <Row gutter={24}>
          <Col lg={8} xs={24}>
            <Heading>{translations(changeStoreMessages.storeImage)}</Heading>
            <AvatarEl>
              <Avatar
                src={imageUrl}
                onUploadImageSuccess={onSetImageUrl}
                ImageControlsComponent={NewImageUploadButton}
                highlight
                customAvatarStyle={newAvatarWrapperStyle}
              />
            </AvatarEl>
          </Col>
          <Col lg={16} xs={24}>
            <InfoContainer>
              <Row gutter={8}>
                <Col lg={12} xs={24}>
                  <FormattedMessage {...messages.storeName}>
                    {(storeName) => (
                      <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: storeName }}>
                        {(fieldRequired) => (
                          <Form.Item
                            name="name"
                            label={storeName}
                            colon={false}
                            rules={[
                              { required: true, message: fieldRequired },
                              { pattern: /[a-zA-Z]/g, message: fieldRequired },
                            ]}
                            initialValue={store.name}
                          >
                            <Input
                              InputComponent={AntdInput}
                              trackingCategory="Edit Store Form | Input"
                              trackingAction="Enter Store name"
                              type="text"
                            />
                          </Form.Item>
                        )}
                      </FormattedMessage>
                    )}
                  </FormattedMessage>
                </Col>
                <Col lg={12} xs={24}>
                  <FormattedMessage {...utilsMessages.phone}>
                    {(phone) => (
                      <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: phone }}>
                        {(fieldRequired) => (
                          <Form.Item
                            name="telephone"
                            label={phone}
                            colon={false}
                            rules={[
                              { required: true, message: fieldRequired },
                              {
                                pattern: /^(?=(?:[()+\-.\s]*\d){9,12}$)[()+\-.,\s\d]*$/,
                                message: translations(messages.invalidPhoneNumber),
                              },
                            ]}
                            initialValue={store.telephone}
                          >
                            <Input
                              InputComponent={AntdInput}
                              trackingCategory="Edit Store Form | Input"
                              trackingAction="Enter Phone"
                              type="text"
                              onBlur={formatPhoneNumber}
                            />
                          </Form.Item>
                        )}
                      </FormattedMessage>
                    )}
                  </FormattedMessage>
                </Col>
              </Row>
              <AddressForMap
                form={form}
                fieldNameFullText={FIELD_NAME_FULL_TEXT}
                fieldName={{
                  city: 'provinceId',
                  district: 'districtId',
                  addressDetail: 'addressDetail',
                  ward: 'wardId',
                  wardId: 'wardId',
                  geoLat: 'geoLat',
                  geoLon: 'geoLon',
                  geoPlusCode: 'geoPlusCode',
                }}
                regions={regions}
                handleShowWarning={handleShowWarning}
                setOpenExtendedMap={setOpenExtendedMap}
                isOpenExtendedMap={openExtendedMap}
                setCoords={setCoords}
                setPlaceId={setPlaceId}
                placeId={placeId}
                setIsOutOfRegion={setIsOutOfRegion}
                setLoading={setLoading}
                isAlreadySubmit={isAlreadySubmit}
              />
            </InfoContainer>
          </Col>
        </Row>
        <DivideLine />
        <Row>
          <Col span={24}>
            <RedInvoiceTitle>{translations(messages.redInvoiceInfo)}</RedInvoiceTitle>
            <RedInvoice form={form} setFetchingInvoice={setFetchingInvoice} />
          </Col>
        </Row>
        {openExtendedMap && coords && (
          <StoreMapWrapper
            form={form}
            fieldNameFullText={{
              addressDetail: FIELD_NAME_FULL_TEXT.addressDetail,
              address: 'addressDetail',
              geoLat: 'geoLat',
              geoLon: 'geoLon',
              geoPlusCode: 'geoPlus',
            }}
            hideMarker={!coords}
            coords={coords}
            setCoords={setCoords}
            placeId={placeId}
            setPlaceId={setPlaceId}
            setIsOutOfRegion={setIsOutOfRegion}
            isOutOfRegion={isOutOfRegion}
            openExtendedMap={openExtendedMap}
            setOpenExtendedMap={setOpenExtendedMap}
            currentRegion={regions?.[0] || ''}
          />
        )}
      </Form>
    </GoogleMapsContextProvider>
  );
};

export default StoreForm;
